/**
 * Single source of truth for the dashboard's runtime configuration.
 *
 * Everything build-tool specific (`import.meta.env.VITE_*`) is normalised here
 * once via fe-commons `resolveEnv`, then enriched with the few dashboard-only
 * knobs (full API URL, region alias, CDN origin, the multi-site URL context).
 * The rest of the app imports `appEnv` / `appConfig` — never `import.meta.env`.
 */
import { resolveEnv, readEnvKey, type AutheEnv } from "@authe/fe-commons/env";
import { resolveAuth0ConfigFromEnv } from "@authe/fe-commons/auth";
import type { AppUrlContext } from "@authe/fe-commons/auth";
import type { Auth0EnvConfig } from "@authe/fe-commons/auth";

const source = import.meta.env as unknown as Record<string, string | boolean | undefined>;

/** Normalised fe-commons env (deploy env, root domain, claims namespace, …). */
export const appEnv: AutheEnv = resolveEnv(source);

/** `true` when running the local multi-site fleet (apps on localhost:300x). */
const local = appEnv.env === "local" || readEnvKey(source, "LOCAL_MULTISITE") === "true";

/**
 * Cross-app URL context: resolves the login site / portals / setup SPA origins.
 * `local` makes them resolve to their localhost ports (login → :3005) instead
 * of prod subdomains, which is exactly the "redirect to the locally-running
 * login site" behaviour the dashboard needs in dev.
 */
export const urlContext: AppUrlContext = {
  rootDomain: appEnv.apiBaseDomain,
  local,
};

/** Resolved Auth0 SPA client config (domain/clientId/audience/namespace). */
export const auth0Config: Auth0EnvConfig = resolveAuth0ConfigFromEnv(appEnv, {
  // Prod uses the custom login.authe.io domain so embedded auth gets same-site
  // cookies; sandbox stays on the canonical tenant domain.
  useCustomDomain: appEnv.env === "prd",
});

export interface AppConfig {
  /** Full API base URL for the active region, e.g. `https://api.sandbox.authe.io`. */
  apiUrl: string;
  /** Optional secondary region URL for the minute-based failover. */
  backupApiUrl?: string;
  /** Region alias (`pdx`, `iad`, `fra`, `dub`). */
  region: string;
  /** CDN origin used to hydrate S3-offloaded large transaction payloads. */
  cdnUrl: string;
  /**
   * Live-stream endpoint (SSE) for `@authe/streaming`. Optional: when unset the
   * dashboard offers Auto-refresh (interval) only and the "Live" push-stream
   * mode stays disabled until the backend endpoint is provisioned. Superseded by
   * {@link appsyncEndpoint} when that is set (AppSync WebSocket is preferred).
   */
  streamingUrl?: string;
  /**
   * AppSync GraphQL realtime endpoint (the streaming hub). When set, the
   * dashboard streams over the AppSync WebSocket transport (`@authe/streaming`)
   * with the active JWT as the lambda auth token, instead of SSE. Optional:
   * unset falls back to {@link streamingUrl} (SSE) or no live mode at all.
   */
  appsyncEndpoint?: string;
  /**
   * AppSync GraphQL HTTPS endpoint for the private-api READ layer (the fast
   * path for composed reads: catalog lists, address overlays, the recent feed).
   * Distinct from {@link appsyncEndpoint} (the streaming hub). Optional: unset
   * means the transport registry keeps every data operation on REST. Set with
   * `PRIVATE_API_GRAPHQL_URL` (the private-api AppSync `graphql_url` output).
   *
   * NOTE: this is the pre-token FALLBACK base only. Once the JWT lands the
   * GraphQL client region-switches to the AppSync custom domain
   * (`https://graphql.{alias}.{continent}.private.{domain}/graphql`) resolved
   * from the same routing config as the REST client — see `app/graphql.tsx`.
   */
  privateApiGraphqlUrl?: string;
  /** Wallet-connect project id + app name for the web3 package. */
  walletConnectProjectId: string;
  walletConnectAppName: string;
}

export const appConfig: AppConfig = {
  apiUrl:
    readEnvKey(source, "API_URL") ??
    `https://api.${appEnv.apiBaseDomain}`,
  backupApiUrl: readEnvKey(source, "BACKUP_API_URL"),
  region: readEnvKey(source, "REGION") ?? "pdx",
  cdnUrl: readEnvKey(source, "CDN_URL") ?? `https://cdn.${appEnv.apiBaseDomain}`,
  streamingUrl: readEnvKey(source, "STREAMING_URL"),
  appsyncEndpoint: readEnvKey(source, "APPSYNC_ENDPOINT"),
  privateApiGraphqlUrl: appEnv.privateApiGraphqlUrl,
  walletConnectProjectId:
    readEnvKey(source, "WALLET_CONNECT_PROJECT_ID") ?? "f5941214a396ccdfb6c5f50c6dfdf8a9",
  walletConnectAppName: readEnvKey(source, "WALLET_CONNECT_APP_NAME") ?? "AuthE",
};

/**
 * CDN origin the seeded PUBLIC assets are served from — the `authe_assets`
 * seeder's `public/GET/cdn/…` keys (chain / token / protocol / bridge logos).
 * Same CloudFront origin as the tx-body CDN unless `ASSET_CDN_BASE` overrides
 * it. Shared by `configureChainLogos` (every `ChainAvatar`) and the graph scene
 * `cdn_key` icon resolution (`SceneGraphView`/`GroupGraphView` `cdnBase`), so
 * both draw the same seeded logos from one source of truth.
 */
export const assetCdnBase: string = readEnvKey(source, "ASSET_CDN_BASE") ?? appConfig.cdnUrl;

export const isLocalMultisite = local;

/** Raw env passthrough for the build-time Casbin policy resolver. */
export const envSource = source;
